import React, { createContext, useContext, useEffect, useReducer, useRef, useCallback } from "react";
import { EMPLOYEES, APPROVALS, ACTIVITY, COLLAB_STEPS } from "@/data/seed";
import { fetchState, saveState } from "@/lib/api";

const LS_KEY = "iq-workforce-state-v1";

// The mutable, persisted slice of the prototype.
function buildSeedState() {
  return {
    employees: JSON.parse(JSON.stringify(EMPLOYEES)),
    approvals: JSON.parse(JSON.stringify(APPROVALS)),
    activity: JSON.parse(JSON.stringify(ACTIVITY)),
    trainingQueue: [],       // rules awaiting approval
    briefingSeen: false,
    hiredCount: 0,
  };
}

const initialState = {
  ...buildSeedState(),
  loaded: false,
  syncing: false,
  // transient (not persisted) collaboration demo visualisation state
  collab: { running: false, step: -1, fromRoom: null, toRoom: null, text: "", who: null },
  // transient desktop-style approval alert
  push: null,
};

const PERSIST_KEYS = ["employees", "approvals", "activity", "trainingQueue", "briefingSeen", "hiredCount"];

// Static identity/config fields (title, department) always come from seed, even
// for already-persisted demo state, so org-structure edits propagate on reload.
// New seed associates that aren't in the persisted state yet are appended too.
function reconcileSeed(payload) {
  if (!payload || !Array.isArray(payload.employees)) return payload;
  const seedById = Object.fromEntries(EMPLOYEES.map((e) => [e.id, e]));
  const present = new Set(payload.employees.map((e) => e.id));
  payload.employees = payload.employees.map((e) => {
    const s = seedById[e.id];
    return s ? { ...e, title: s.title, department: s.department } : e;
  });
  for (const s of EMPLOYEES) {
    if (!present.has(s.id)) payload.employees.push({ ...s });
  }
  return payload;
}

function nowTime() {
  return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function reducer(state, action) {
  switch (action.type) {
    case "HYDRATE":
      return { ...state, ...action.payload, loaded: true };
    case "SET_SYNCING":
      return { ...state, syncing: action.value };

    case "SET_STATUS":
      return {
        ...state,
        employees: state.employees.map((e) =>
          e.id === action.id ? { ...e, status: action.status } : e
        ),
      };

    case "BULK_STATUS":
      return {
        ...state,
        employees: state.employees.map((e) =>
          action.map[e.id] ? { ...e, status: action.map[e.id] } : e
        ),
      };

    case "ADD_ACTIVITY":
      return { ...state, activity: [{ id: `a-${Date.now()}`, time: nowTime(), ...action.entry }, ...state.activity] };

    case "ASSIGN_TASK":
      return {
        ...state,
        employees: state.employees.map((e) =>
          e.id === action.id
            ? { ...e, assignment: action.task, status: "working", openAssignments: e.openAssignments + 1 }
            : e
        ),
      };

    case "RESOLVE_APPROVAL":
      return {
        ...state,
        approvals: state.approvals.map((a) =>
          a.id === action.id ? { ...a, status: action.status } : a
        ),
      };

    case "ADD_APPROVAL":
      return { ...state, approvals: [action.approval, ...state.approvals] };

    case "HIRE":
      return {
        ...state,
        employees: [...state.employees, action.employee],
        hiredCount: state.hiredCount + 1,
      };

    case "QUEUE_RULE":
      return { ...state, trainingQueue: [action.rule, ...state.trainingQueue] };

    case "APPROVE_RULE": {
      const rule = state.trainingQueue.find((r) => r.id === action.id);
      return {
        ...state,
        trainingQueue: state.trainingQueue.filter((r) => r.id !== action.id),
        employees: state.employees.map((e) =>
          rule && e.id === rule.employeeId
            ? { ...e, trainingHistory: [{ date: new Date().toISOString().slice(0, 10), rule: rule.text }, ...e.trainingHistory] }
            : e
        ),
      };
    }

    case "SET_BRIEFING_SEEN":
      return { ...state, briefingSeen: true };

    case "COLLAB":
      return { ...state, collab: { ...state.collab, ...action.payload } };

    case "PUSH":
      return { ...state, push: action.payload };
    case "CLEAR_PUSH":
      return { ...state, push: null };

    case "RESET":
      return { ...buildSeedState(), loaded: true, syncing: false, collab: initialState.collab, push: null };

    default:
      return state;
  }
}

const StoreContext = createContext(null);

export function StoreProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const saveTimer = useRef(null);
  const collabTimers = useRef([]);

  // ---- hydrate: backend -> localStorage -> seed ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let payload = null;
      try {
        payload = await fetchState();
      } catch (_) {
        // backend unreachable — fall back to cache
      }
      if (!payload) {
        try {
          const cached = localStorage.getItem(LS_KEY);
          if (cached) payload = JSON.parse(cached);
        } catch (_) {}
      }
      if (!payload) payload = buildSeedState();
      if (!cancelled) dispatch({ type: "HYDRATE", payload: reconcileSeed(payload) });
    })();
    return () => {
      cancelled = true;
      collabTimers.current.forEach(clearTimeout);
    };
  }, []);

  // ---- persist (debounced) to localStorage + backend ----
  useEffect(() => {
    if (!state.loaded) return;
    const persistable = PERSIST_KEYS.reduce((acc, k) => ((acc[k] = state[k]), acc), {});
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(persistable));
    } catch (_) {}
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        dispatch({ type: "SET_SYNCING", value: true });
        await saveState(persistable);
      } catch (_) {
        // stays in localStorage; backend will catch up next change
      } finally {
        dispatch({ type: "SET_SYNCING", value: false });
      }
    }, 600);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.employees, state.approvals, state.activity, state.trainingQueue, state.briefingSeen, state.hiredCount, state.loaded]);

  // ---- collaboration demo runner ----
  const runCollab = useCallback((onStep) => {
    collabTimers.current.forEach(clearTimeout);
    collabTimers.current = [];
    dispatch({ type: "COLLAB", payload: { running: true, step: 0 } });

    COLLAB_STEPS.forEach((step, i) => {
      const t = setTimeout(() => {
        dispatch({
          type: "COLLAB",
          payload: { step: i, fromRoom: step.fromRoom, toRoom: step.toRoom, text: step.text, who: step.who },
        });
        if (step.status && Object.keys(step.status).length) {
          dispatch({ type: "BULK_STATUS", map: step.status });
        }
        dispatch({
          type: "ADD_ACTIVITY",
          entry: { employee: step.who, task: step.text, source: "Collaboration Demo", action: "Automated hand-off", consulted: [], approval: "N/A", result: "In progress", icon: "Workflow" },
        });
        if (step.toRoom === "approval") {
          dispatch({ type: "PUSH", payload: { title: "Field-service visit · AED CS-1048", employeeId: "miles", ts: Date.now() } });
        }
        onStep && onStep(i, step);
      }, i * 1700);
      collabTimers.current.push(t);
    });

    // finish: restore statuses + ensure the field-service approval exists
    const end = setTimeout(() => {
      dispatch({ type: "BULK_STATUS", map: { ava: "working", atlas: "working", nora: "working", miles: "waiting_info" } });
      dispatch({ type: "COLLAB", payload: { running: false, step: -1, fromRoom: null, toRoom: null, text: "", who: null } });
    }, COLLAB_STEPS.length * 1700 + 800);
    collabTimers.current.push(end);
  }, []);

  const value = {
    state,
    dispatch,
    runCollab,
    // convenience action creators
    setStatus: (id, status) => dispatch({ type: "SET_STATUS", id, status }),
    addActivity: (entry) => dispatch({ type: "ADD_ACTIVITY", entry }),
    assignTask: (id, task) => dispatch({ type: "ASSIGN_TASK", id, task }),
    resolveApproval: (id, status) => dispatch({ type: "RESOLVE_APPROVAL", id, status }),
    addApproval: (approval) => dispatch({ type: "ADD_APPROVAL", approval }),
    hire: (employee) => dispatch({ type: "HIRE", employee }),
    queueRule: (rule) => dispatch({ type: "QUEUE_RULE", rule }),
    approveRule: (id) => dispatch({ type: "APPROVE_RULE", id }),
    setBriefingSeen: () => dispatch({ type: "SET_BRIEFING_SEEN" }),
    clearPush: () => dispatch({ type: "CLEAR_PUSH" }),
    reset: () => dispatch({ type: "RESET" }),
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore() {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error("useStore must be used within StoreProvider");
  return ctx;
}

export function useEmployee(id) {
  const { state } = useStore();
  return state.employees.find((e) => e.id === id);
}
